import { useEffect, useRef, useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { loadScript } from "../lib/script";

const TURNSTILE_SCRIPT = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
const RESEND_WAIT_SECONDS = 60;

export function EmailSignIn() {
  const container = useRef<HTMLDivElement>(null);
  const widgetId = useRef<string | undefined>(undefined);
  const verifying = useRef(false);
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [step, setStep] = useState<"request" | "code">("request");
  const [email, setEmail] = useState("");
  const [requestId, setRequestId] = useState("");
  const [emailHint, setEmailHint] = useState("");
  const [code, setCode] = useState("");
  const [resendIn, setResendIn] = useState(0);
  const [challengeToken, setChallengeToken] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const siteKey = import.meta.env.VITE_TURNSTILE_SITE_KEY;

  useEffect(() => {
    if (step !== "request" || !siteKey || !container.current) return;
    let active = true;
    void loadScript(TURNSTILE_SCRIPT).then(() => {
      if (!active || !container.current || !window.turnstile) return;
      widgetId.current = window.turnstile.render(container.current, {
        sitekey: siteKey,
        action: "email-login",
        theme: "light",
        size: "flexible",
        callback: setChallengeToken,
        "expired-callback": () => setChallengeToken(""),
        "error-callback": () => {
          setChallengeToken("");
          setError("The security check could not load. Please try again.");
        }
      });
    }).catch(() => setError("The security check could not load. Please try again."));
    return () => {
      active = false;
      if (widgetId.current && window.turnstile) window.turnstile.remove(widgetId.current);
      widgetId.current = undefined;
    };
  }, [siteKey, step]);

  useEffect(() => {
    if (step !== "code" || resendIn <= 0) return;
    const timer = window.setTimeout(() => setResendIn((value) => Math.max(0, value - 1)), 1_000);
    return () => window.clearTimeout(timer);
  }, [resendIn, step]);

  const requestCode = async (event: FormEvent) => {
    event.preventDefault();
    if (!challengeToken) {
      setError("Complete the security check first.");
      return;
    }
    setSubmitting(true);
    setError("");
    try {
      const result = await api.requestEmailLogin(email, challengeToken);
      setRequestId(result.requestId);
      setEmailHint(result.emailHint);
      setCode("");
      setResendIn(RESEND_WAIT_SECONDS);
      setStep("code");
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "The sign-in email could not be sent.");
      setChallengeToken("");
      if (widgetId.current && window.turnstile) window.turnstile.reset(widgetId.current);
    } finally {
      setSubmitting(false);
    }
  };

  const verifyCode = async (value = code) => {
    if (verifying.current || !/^\d{6}$/.test(value)) return;
    verifying.current = true;
    setSubmitting(true);
    setError("");
    try {
      const user = await api.verifyEmailCode(requestId, value);
      setUser(user);
      navigate("/account", { replace: true });
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "The code could not be verified.");
    } finally {
      verifying.current = false;
      setSubmitting(false);
    }
  };

  const updateCode = (value: string) => {
    const nextCode = value.replace(/\D/g, "").slice(0, 6);
    setCode(nextCode);
    setError("");
    if (nextCode.length === 6) void verifyCode(nextCode);
  };

  const restartRequest = () => {
    setStep("request");
    setRequestId("");
    setEmailHint("");
    setCode("");
    setChallengeToken("");
    setError("");
  };

  if (!siteKey) return <div className="config-note">Email sign-in is awaiting its production security configuration.</div>;
  if (step === "code") return <form className="email-signin email-code-panel" onSubmit={(event) => { event.preventDefault(); void verifyCode(); }}>
    <p className="email-code-heading">Enter your 6-digit code</p>
    <p className="summary-note">We sent it to <strong>{emailHint}</strong>. It expires in 10 minutes.</p>
    <label className="sr-only" htmlFor="signin-code">6-digit sign-in code</label>
    <input id="signin-code" className="email-code-input" type="text" inputMode="numeric" autoComplete="one-time-code" pattern="[0-9]*" maxLength={6} autoFocus value={code} onChange={(event) => updateCode(event.target.value)} placeholder="000000" aria-describedby="signin-code-help" />
    <button className="button primary full" type="submit" disabled={submitting || code.length !== 6}>{submitting ? "Verifying…" : "Verify and sign in"}</button>
    <p id="signin-code-help" className="summary-note">You can also use the secure link in the email to sign in on the device where you open it.</p>
    <div className="email-code-actions">
      <button className="text-button" type="button" disabled={submitting} onClick={restartRequest}>Change email</button>
      <button className="text-button" type="button" disabled={submitting || resendIn > 0} onClick={restartRequest}>{resendIn > 0 ? `New code in ${resendIn}s` : "Request a new code"}</button>
    </div>
    {error && <p className="form-error" role="alert">{error}</p>}
  </form>;

  return <form className="email-signin" onSubmit={(event) => void requestCode(event)}>
    <label htmlFor="signin-email">Email address</label>
    <input id="signin-email" type="email" autoComplete="email" required maxLength={254} value={email} onChange={(event) => setEmail(event.target.value)} placeholder="you@example.com" />
    <div className="turnstile-slot" ref={container} />
    <button className="button secondary full" type="submit" disabled={submitting || !email.trim() || !challengeToken}>{submitting ? "Sending code…" : "Email me a sign-in code"}</button>
    <p className="summary-note">No password needed. We will email you a 6-digit code and a secure backup link.</p>
    {error && <p className="form-error" role="alert">{error}</p>}
  </form>;
}
